import type { ColumnDef, FilterFnOption } from '@tanstack/react-table'
import { useMemo } from 'react'
import { FBPage } from '../../components/FbUI/Page/FBPage'
import {
  Upload,
  Save,
  CheckCircle,
  Download,
  ArrowLeft,
  X,
  Tag,
  Briefcase,
  User,
  Loader2,
  Users2,
  StickyNote,
} from 'lucide-react'
import { Link } from 'react-router-dom'
import FbButton from 'components/FbUI/FbButton'
import { Select } from 'components/UI/Select/Select'
import { ColumnType, CSVRow, CSVRowState } from './importer-types'
import { ChainSearchResult, DoorSearchResult } from 'features/elastic/searchers'
import { ImporterDataTable } from './importer-datatable'
import { HelpTooltip } from 'components/Tooltip/HelpTooltip'
import { cn } from 'components/UI/cn'
import { Tabs } from 'components/FbUI/Tabs'
import { Switch } from 'components/UI/Switch/Switch'
import { ManualSearchModal } from './ManualSearchModal'
import { ValidateModal } from './validate-modal'
import { Modal } from 'components/UI/Modal/Modal'
import { ImporterMatchModal } from './ImporterMatchModal'
import {
  columnOptions,
  columnTypeColors,
  useImporterViewModel,
} from './ImporterViewModel'
import { FbLink } from 'components/FbUI/FbLink'
import { oppTypesMap } from 'utils/oppTypesMap'
import { FileDragDrop } from 'components/FileDragDrop'
import { BaseExportModal } from 'components/Modals/ExportModal/ExportModal'
import { BaseTextInput } from 'components/FormUtils/TextInput/text-input'

export default function Importer(props: { importNumber: number | null }) {
  const viewModel = useImporterViewModel(props)

  const columns = useMemo(() => {
    if (!viewModel.importData) return []

    const headers = viewModel.importData.headers

    const filterFnMap: Record<string, FilterFnOption<CSVRow>> = {
      __internal__existing_chain_proxy_subcategory_id: 'equals',
    }

    const crmDataIndicatorColumnKeys: (keyof CSVRow)[] = [
      '__internal__tag_set',
      '__internal__deal_set',
      '__internal__contact_set',
      '__internal__company_notes_set',
    ]

    const columnDefs: ColumnDef<CSVRow>[] = headers
      .filter((h) => !crmDataIndicatorColumnKeys.includes(h.original as any))
      .toSorted((a, b) => {
        if (a.original.startsWith('__internal__')) {
          return -1
        }
        if (b.original.startsWith('__internal__')) {
          return 1
        }
        return 0
      })
      .map((header) => ({
        id: header.original,
        accessorKey: header.original,
        size: 300,
        filterFn: filterFnMap[header.original],
        header: () => {
          if (header.original.startsWith('__internal__')) {
            return (
              <div className="text-xs text-gray-500">
                {header.label ?? header.original}
              </div>
            )
          }

          return (
            <div>
              <div className="flex flex-row items-center gap-2">
                <Select
                  buttonSize="xs"
                  onSelect={(value) =>
                    viewModel.updateMap(header.original, value)
                  }
                  selected={header.mapped}
                  disabled={viewModel.importPreview?.from_unmatched_chains}
                  nullSelectionLabel="Select a Column"
                  items={Object.entries(columnOptions).map(([k, v]) => ({
                    label: v.name,
                    value: k,
                    hidden:
                      viewModel.mappedColumns?.includes(k) &&
                      k !== 'company_notes',
                  }))}
                />
                {!viewModel.importPreview?.from_unmatched_chains && (
                  <FbButton
                    variant="outline"
                    size="xs"
                    onClick={() =>
                      viewModel.updateMap(header.original, 'ignore')
                    }
                    disabled={viewModel.importPreview?.from_unmatched_chains}
                  >
                    <X size={16} />
                  </FbButton>
                )}
              </div>
              <pre className="text-xs truncate max-w-[200px] pt-2">
                {header.label ?? header.original}
              </pre>
            </div>
          )
        },
        cell: (info) => {
          const row = info.row.original

          if (header.original === '__internal__set_chain_proxy_id') {
            let firstbiteId =
              (row.__internal__matched_chain?.type ?? '') +
              (row.__internal__matched_chain?.item_id ?? '')
            if (row.__internal__is_custom_company) {
              firstbiteId = 'UG' + row.__internal__set_chain_proxy_id
            }
            return (
              <div
                key={info.cell.id}
                className="h-full w-full p-2 leading-none flex items-center justify-start"
              >
                <FbLink
                  to={`${oppTypesMap[(firstbiteId.substring(0, 2) as keyof typeof oppTypesMap) || 'UG']?.chainPageUrl ?? '/company/'}${firstbiteId}`}
                >
                  {row.__internal__matched_chain?.name ?? firstbiteId}
                </FbLink>
              </div>
            )
          }

          if (
            header.original ===
            '__internal__existing_chain_proxy_subcategory_id'
          ) {
            const subCategory =
              viewModel.chainMergerAvailableSubcategories[
                info.row.original
                  .__internal__existing_chain_proxy_subcategory_id
              ] ?? ''
            return (
              <div
                key={info.cell.id}
                className="h-full w-full p-2 leading-none flex items-center justify-start"
              >
                <div className="text-sm truncate">{subCategory}</div>
              </div>
            )
          }

          const mappedColumn = header.mapped
          const columnType = (columnOptions[mappedColumn]?.type ??
            'ignore') as ColumnType
          const color = columnTypeColors[columnType]
          return (
            <div
              key={info.cell.id}
              style={{
                backgroundColor: color.background,
                color: color.text,
              }}
              className="h-full w-full p-2 leading-none flex items-center justify-start"
            >
              {info.getValue() as React.ReactNode}
            </div>
          )
        },
      }))

    // MATCHES SELECTOR
    if (viewModel.selectedTab !== 'set') {
      if (!viewModel.importPreview?.from_unmatched_chains) {
        columnDefs.unshift({
          id: 'is_custom_company',
          header: 'Custom',
          size: 270,
          cell: (info) => {
            const row = info.row.original
            const disabled = row.__internal__set_chain_proxy_id !== -1
            return (
              <div
                key={info.cell.id}
                className={cn(
                  'flex-1 flex gap-2 items-center justify-start h-full w-full p-2',
                  { 'opacity-20': disabled }
                )}
              >
                <Switch
                  checked={row.__internal__is_custom_company}
                  disabled={disabled}
                  onChange={(e) => {
                    viewModel.updateIsCustomCompany(
                      [row.__internal__row_id],
                      e.target.checked
                    )
                  }}
                ></Switch>
                <Select
                  buttonSize="xs"
                  className="w-[400px] max-h-[500px]"
                  disabled={!row.__internal__is_custom_company || disabled}
                  items={viewModel.companySubcategories}
                  selected={row.__internal__custom_company_sub_category_id}
                  nullSelectionLabel="Select a Category"
                  onSelect={(value, originalItem) =>
                    viewModel.updateCustomCompanyCategory(
                      [row.__internal__row_id],
                      originalItem?.category_id,
                      value
                    )
                  }
                />
              </div>
            )
          },
        })
      }

      columnDefs.unshift(
        {
          id: 'matched_chain',
          accessorKey: 'matched_chain',
          header: 'Chain Match',
          size: 250,
          cell: (info) => {
            const row = info.row.original
            const isImported = row.__internal__set_chain_proxy_id !== -1
            const disabled = isImported || row.__internal__is_custom_company

            return (
              <div
                key={info.cell.id}
                className={cn(
                  'flex-1 flex gap-2 items-center justify-start h-full w-full p-2',
                  { 'opacity-20': disabled }
                )}
              >
                {row.__internal__matched_chain && (
                  <HelpTooltip
                    content={
                      <ChainInfo chain={row.__internal__matched_chain} />
                    }
                    delay={[0, 100]}
                  />
                )}
                {row.__internal__is_chain_manual_match ? (
                  <div className="text-sm truncate">
                    {row.__internal__matched_chain?.name}
                  </div>
                ) : (
                  <Select
                    buttonSize="xs"
                    className="w-[400px] max-h-[500px] flex-1"
                    buttonClassName="max-w-[300px] flex-1"
                    buttonVariant={
                      row.__internal__matched_chain?.item_id
                        ? 'primary'
                        : 'outline'
                    }
                    disabled={
                      !row.__internal__suggested_chains?.length || disabled
                    }
                    nullSelectionLabel={
                      row.__internal__suggested_chains?.length
                        ? `Found ${row.__internal__suggested_chains?.length} chain suggestions`
                        : 'No Suggestions'
                    }
                    items={row.__internal__suggested_chains?.map((chain) => ({
                      label: <ChainInfo chain={chain} />,
                      value: chain.item_id,
                      labelWhenSelected: (
                        <div className="flex flex-row gap-2">
                          <div className="text-sm truncate">{chain.name}</div>
                        </div>
                      ),
                    }))}
                    onSelect={(value) => {
                      const chain = row.__internal__suggested_chains?.find(
                        (c) => c.item_id === value
                      )
                      if (chain) {
                        viewModel.updateMatchedChain(
                          row.__internal__row_id,
                          chain,
                          false
                        )
                      }
                    }}
                    selected={row.__internal__matched_chain?.item_id}
                  />
                )}
                <FbButton
                  variant="outline"
                  disabled={disabled}
                  size="xs"
                  onClick={() => {
                    viewModel.updateMatchedChain(
                      row.__internal__row_id,
                      null,
                      false
                    )
                    viewModel.updateMatchedDoor(
                      row.__internal__row_id,
                      null,
                      false
                    )
                  }}
                >
                  Clear
                </FbButton>
              </div>
            )
          },
        },
        {
          id: 'matched_door',
          accessorKey: 'matched_door',
          header: 'Door Match',
          size: 300,
          cell: (info) => {
            const row = info.row.original
            const isImported = row.__internal__set_chain_proxy_id !== -1
            const disabled = isImported || row.__internal__is_custom_company
            const doors = row.__internal__suggested_doors?.filter((c) => {
              if (row.__internal__matched_chain) {
                return c.chain_id === row.__internal__matched_chain.item_id
              }
              return true
            })

            return (
              <div
                key={info.cell.id}
                className={cn(
                  'flex-1 flex gap-2 items-center justify-start h-full w-full p-2',
                  {
                    'opacity-20': disabled,
                  }
                )}
              >
                {row.__internal__matched_door && (
                  <HelpTooltip
                    content={<DoorInfo door={row.__internal__matched_door} />}
                    delay={[0, 100]}
                  />
                )}
                {row.__internal__is_door_manual_match ||
                row.__internal__is_chain_manual_match ? (
                  <div className="text-sm truncate">
                    {row.__internal__matched_door?.name ?? 'No Door Selected'}
                  </div>
                ) : (
                  <Select
                    buttonSize="xs"
                    buttonVariant={
                      row.__internal__matched_door?.item_id
                        ? 'primary'
                        : 'outline'
                    }
                    className="w-[400px] max-h-[500px]"
                    disabled={
                      !row.__internal__suggested_doors?.length || disabled
                    }
                    nullSelectionLabel={
                      row.__internal__matched_chain?.item_id
                        ? `${row.__internal__suggested_doors?.length ?? 0} doors for selected chain`
                        : row.__internal__suggested_doors?.length
                          ? `Found ${row.__internal__suggested_doors?.length ?? 0} door suggestions`
                          : 'No Suggestions'
                    }
                    items={doors?.map((door) => ({
                      label: <DoorInfo door={door} />,
                      labelWhenSelected: (
                        <div className="flex flex-row gap-2">
                          <div className="text-sm truncate">{door.name}</div>
                        </div>
                      ),
                      value: door.item_id,
                    }))}
                    onSelect={(value) => {
                      const door = row.__internal__suggested_doors?.find(
                        (d) => d.item_id === value
                      )
                      if (door) {
                        const chain =
                          row.__internal__suggested_chains?.find(
                            (c) => c.item_id === door?.chain_id
                          ) ?? null
                        viewModel.updateMatchedDoor(
                          row.__internal__row_id,
                          door,
                          false
                        )
                        viewModel.updateMatchedChain(
                          row.__internal__row_id,
                          chain,
                          false
                        )
                      }
                    }}
                    selected={row.__internal__matched_door?.item_id}
                  />
                )}
                <FbButton
                  variant="outline"
                  disabled={disabled}
                  size="xs"
                  onClick={() =>
                    viewModel.updateMatchedDoor(
                      row.__internal__row_id,
                      null,
                      false
                    )
                  }
                >
                  Clear
                </FbButton>
                <FbButton
                  variant="outline"
                  size="xs"
                  onClick={() =>
                    viewModel.startManualSearch(row.__internal__row_id)
                  }
                >
                  Manual Search
                </FbButton>
              </div>
            )
          },
        }
      )
    }

    if (viewModel.selectedTab === 'set') {
      const crmDataIndicatorColumnDefs: ColumnDef<CSVRow>[] = headers
        .filter((h) => crmDataIndicatorColumnKeys.includes(h.original as any))
        .map((header) => ({
          id: header.original,
          accessorKey: header.original,
          size: 130,
          header: () => {
            return (
              <div className="text-xs text-gray-500">
                {header.label ?? header.original}
              </div>
            )
          },
          cell: (info) => {
            return (
              <div className="text-xs text-gray-500 flex-1 flex gap-2 items-center justify-start h-full w-full p-2">
                {info.getValue() !== -1 ? <CheckCircle size={16} /> : '-'}
              </div>
            )
          },
        }))

      columnDefs.unshift(...crmDataIndicatorColumnDefs)
    }

    return columnDefs ?? []
  }, [viewModel.importData, viewModel.importPreview, viewModel.selectedTab])

  if (
    viewModel.isUploading ||
    (!!props.importNumber &&
      (viewModel.isLoading ||
        viewModel.isLoadingStatus ||
        viewModel.isFetching))
  ) {
    return (
      <div className="flex flex-col w-full h-full justify-center items-center">
        <div className="text-sm flex flex-row gap-2 items-center">
          <Loader2 className="animate-spin" />
          Loading...
        </div>
      </div>
    )
  }

  if (viewModel.importPreviewStatus?.status === 'running') {
    return (
      <div className="flex flex-col w-full h-full justify-center items-center">
        <div className="text-sm">
          {viewModel.importPreviewStatus.process_running} in progress, wait or
          come back later...
        </div>
      </div>
    )
  }

  if (!props.importNumber) {
    return (
      <div className="border-2 border-dashed border-gray-300 rounded-md p-4 flex flex-col w-full h-full justify-center items-center">
        <div className="p-4 w-full">
          {!props.importNumber && (
            <FileDragDrop
              file={viewModel.rawFile}
              onFileChange={viewModel.handleFileUpload}
            />
          )}
        </div>
        <ImporterMatchModal
          data={viewModel.importData}
          open={viewModel.showHeaderMappingModal}
          onClose={() => viewModel.setShowHeaderMappingModal(false)}
          onSave={async (data) => {
            viewModel.setShowHeaderMappingModal(false)
            await viewModel.handleSave(data)
          }}
        />
      </div>
    )
  }

  return (
    <FBPage>
      <div className="flex flex-row gap-2 items-start justify-end">
        <Tabs
          isSmall
          tabs={[
            {
              key: 'unmatched',
              title: `Unmatched (${viewModel.tableData.unmatched.length})`,
            },
            {
              key: 'matched',
              title: `Matched (${viewModel.tableData.matched.length})`,
            },
            {
              key: 'set',
              title: `${viewModel.importPreview?.from_unmatched_chains ? 'Merged' : 'Set'} (${viewModel.tableData.set.length})`,
            },
          ]}
          selectedTab={viewModel.selectedTab}
          setSelectedTab={(tab) => {
            viewModel.setSelectedTab(tab as CSVRowState)
          }}
        />

        <BackButton hasPendingChanges={viewModel.hasPendingChanges} />

        <FbButton
          onClick={() => viewModel.handleSave()}
          variant="primary"
          size="sm"
          className="flex items-center gap-2 flex-shrink-0"
          disabled={!viewModel.hasPendingChanges}
          loading={viewModel.isSaving}
        >
          <Save size={16} />
          Save
        </FbButton>

        <FbButton
          onClick={() => viewModel.setShowConfigureExportModal(true)}
          variant="secondary"
          size="sm"
          className="flex items-center gap-2 flex-shrink-0"
        >
          <Download size={16} />
          Export
        </FbButton>
      </div>
      <div
        style={{
          height: 'calc(var(--base-page-table-height) - 30px)',
        }}
      >
        <ImporterDataTable
          tableHeader={
            <div className="flex flex-row gap-2 justify-between py-2 w-full">
              <div className="flex flex-row gap-2">
                {viewModel.importPreview?.from_unmatched_chains && (
                  <div className="flex flex-row gap-2">
                    <Select
                      buttonSize="sm"
                      className="w-[400px] max-h-[500px]"
                      nullSelectionLabel="Filter by Opp Type"
                      items={Object.entries(
                        viewModel.chainMergerAvailableSubcategories
                      ).map(([value, label]) => ({
                        label,
                        value,
                      }))}
                      onSelect={(value) =>
                        viewModel.setSubcategoryFilter(Number(value))
                      }
                      selected={viewModel.selectedSubcategoryFilter?.toString()}
                    />
                  </div>
                )}

                {viewModel.selectedTab !== 'set' ? (
                  <>
                    {!viewModel.importPreview?.from_unmatched_chains && (
                      <FbButton
                        disabled={!viewModel.shouldShowBulkActions}
                        size="sm"
                        variant="secondary"
                        onClick={() =>
                          viewModel.updateIsCustomCompany(
                            viewModel.selectedRowIds,
                            false
                          )
                        }
                      >
                        Unset Custom Company
                      </FbButton>
                    )}

                    {!viewModel.importPreview?.from_unmatched_chains && (
                      <Select
                        buttonSize="sm"
                        className="w-[400px] max-h-[500px]"
                        disabled={!viewModel.shouldShowBulkActions}
                        items={viewModel.companySubcategories}
                        nullSelectionLabel="Set as Custom Company"
                        onSelect={(value, originalItem) =>
                          viewModel.updateCustomCompanyCategory(
                            viewModel.selectedRowIds,
                            originalItem?.category_id,
                            value
                          )
                        }
                      />
                    )}
                  </>
                ) : (
                  <div></div>
                )}
              </div>

              <div className="flex flex-row gap-2">
                {viewModel.selectedTab === 'unmatched' && (
                  <FbButton
                    onClick={viewModel.handleMatch}
                    variant="warning"
                    size="sm"
                    className="flex items-center gap-2 flex-shrink-0"
                    loading={viewModel.isMatching}
                    disabled={
                      !props.importNumber ||
                      viewModel.tableData['set'].length ===
                        viewModel.importData?.rows.length
                    }
                  >
                    <CheckCircle size={16} />
                    Start Match
                  </FbButton>
                )}

                {viewModel.selectedTab === 'matched' && (
                  <>
                    {viewModel.importPreview?.from_unmatched_chains ? (
                      <FbButton
                        onClick={viewModel.handleSetUnmatchedChains}
                        variant="secondary"
                        size="sm"
                        className="flex items-center gap-2 flex-shrink-0"
                        loading={viewModel.isSettingUnmatchedChains}
                        disabled={
                          !props.importNumber ||
                          viewModel.tableData['matched'].length === 0
                        }
                      >
                        <Users2 size={16} />
                        Merge Unmatched Chains
                      </FbButton>
                    ) : (
                      <FbButton
                        onClick={viewModel.handleSetCompanies}
                        variant="secondary"
                        size="sm"
                        className="flex items-center gap-2"
                        loading={viewModel.isSettingCompanies}
                        disabled={
                          !props.importNumber ||
                          viewModel.tableData['matched'].length === 0
                        }
                      >
                        <Upload size={16} />
                        Set Companies
                      </FbButton>
                    )}
                  </>
                )}

                {viewModel.selectedTab === 'set' && (
                  <div className="flex flex-row items-center gap-2 ml-2">
                    <FbButton
                      onClick={viewModel.handleSetTags}
                      variant="secondary"
                      size="sm"
                      className="flex items-center gap-2 flex-shrink-0"
                      loading={viewModel.isSettingTags}
                      disabled={
                        !props.importNumber ||
                        viewModel.tableData['set'].length === 0 ||
                        !viewModel.canAddTags[0]
                      }
                      tooltip={
                        viewModel.canAddTags[1].length > 0
                          ? viewModel.canAddTags[1].join('\n')
                          : undefined
                      }
                    >
                      <Tag size={16} />
                      Import Tags
                    </FbButton>

                    <FbButton
                      onClick={viewModel.handleSetDeals}
                      variant="secondary"
                      size="sm"
                      className="flex items-center gap-2 flex-shrink-0"
                      loading={viewModel.isSettingDeals}
                      disabled={
                        !props.importNumber ||
                        viewModel.tableData['set'].length === 0 ||
                        !viewModel.canAddDeals[0]
                      }
                      tooltip={
                        viewModel.canAddDeals[1].length > 0
                          ? viewModel.canAddDeals[1].join('\n')
                          : undefined
                      }
                    >
                      <Briefcase size={16} />
                      Import Deals
                    </FbButton>

                    <FbButton
                      onClick={viewModel.handleSetContacts}
                      variant="secondary"
                      size="sm"
                      className="flex items-center gap-2 flex-shrink-0"
                      loading={viewModel.isSettingContacts}
                      disabled={
                        !props.importNumber ||
                        viewModel.tableData['set'].length === 0 ||
                        !viewModel.canAddContacts[0]
                      }
                      tooltip={
                        viewModel.canAddContacts[1].length > 0
                          ? viewModel.canAddContacts[1].join('\n')
                          : undefined
                      }
                    >
                      <User size={16} />
                      Import Contacts
                    </FbButton>

                    <FbButton
                      onClick={viewModel.handleSetCompanyNotes}
                      variant="secondary"
                      size="sm"
                      className="flex items-center gap-2 flex-shrink-0"
                      loading={viewModel.isSettingCompanyNotes}
                      disabled={
                        !props.importNumber ||
                        viewModel.tableData['set'].length === 0 ||
                        !viewModel.canAddCompanyNotes[0]
                      }
                      tooltip={
                        viewModel.canAddCompanyNotes[1].length > 0
                          ? viewModel.canAddCompanyNotes[1].join('\n')
                          : undefined
                      }
                    >
                      <StickyNote size={16} />
                      Import Company Notes
                    </FbButton>
                  </div>
                )}
              </div>
            </div>
          }
          data={viewModel.tableData[viewModel.selectedTab]}
          columns={columns}
          setRowSelection={viewModel.setRowSelection}
          columnFiltering={viewModel.columnFiltering}
        />
      </div>

      <ManualSearchModal
        show={viewModel.selectedRowId !== null}
        onClose={() => {
          viewModel.setSelectedRowId(null)
        }}
        onSelect={viewModel.handleManualSearchSelect}
      />

      <ValidateModal
        open={!!viewModel.validateModalData}
        onClose={() => viewModel.setValidateModalData(null)}
        onValidate={(data) => {
          viewModel.handleValidation(data.changes, data.columnName)
        }}
        data={viewModel.validateModalData}
      />

      {/* Select Deal Product Modal */}
      <Modal
        open={viewModel.showSelectDealProductModal}
        disableCloseIcon
        title="Select Deal Product"
        description="The deal import requires a product to be selected. Since the product column is not present in the uploaded CSV, you must select the product from the dropdown below."
        size="lg"
        blockAccept={viewModel.selectedDealProduct == null}
        acceptButtonText="Select Deal Product"
        onAccept={() => {
          if (viewModel.selectedDealProduct) {
            viewModel.handleSelectDealProduct(viewModel.selectedDealProduct)
          }
        }}
        onCancel={() => {
          viewModel.setShowSelectDealProductModal(false)
          viewModel.setSelectedDealProduct(null)
        }}
      >
        <Select
          className="w-full"
          items={viewModel.products?.map((product) => ({
            label: product.name,
            value: product.name,
          }))}
          selected={viewModel.selectedDealProduct}
          onSelect={(value) => viewModel.setSelectedDealProduct(value)}
        />
      </Modal>

      <Modal
        open={viewModel.showConfigureExportModal}
        title="Configure Export"
        description="Configure the export options for the importer."
        size="lg"
        onCancel={() => viewModel.setShowConfigureExportModal(false)}
        acceptButtonText="Done"
        onAccept={() => viewModel.handleConfigureExport()}
      >
        <div className="flex flex-col gap-6 p-2">
          {/* Suggestions Toggle */}
          <div className="flex flex-row items-center gap-3">
            <Switch
              checked={viewModel.exportConfig.allowSuggestions}
              onChange={(e) =>
                viewModel.setExportConfig({
                  ...viewModel.exportConfig,
                  allowSuggestions: e.target.checked,
                })
              }
            />
            <div className="font-medium text-gray-800">
              Include Suggested Data
            </div>
          </div>

          {/* Suggestion Amount */}
          <div className="flex flex-col gap-1">
            <label
              htmlFor="suggestionAmount"
              className={`text-sm font-medium text-gray-700 ${
                !viewModel.exportConfig.allowSuggestions ? 'opacity-50' : ''
              }`}
            >
              Suggestion Amount
            </label>
            <BaseTextInput
              value={viewModel.exportConfig.suggestionAmount}
              name="suggestionAmount"
              type="number"
              min={1}
              max={10}
              disabled={!viewModel.exportConfig.allowSuggestions}
              onChange={(value) =>
                viewModel.setExportConfig({
                  ...viewModel.exportConfig,
                  suggestionAmount: Number(value),
                })
              }
              className="w-32"
            />
            <span className="text-xs text-gray-500">
              Number of suggestions to include per row (1-10)
            </span>
          </div>
        </div>
      </Modal>

      <BaseExportModal
        title="Select Columns to Export"
        subTitle="Export up to 10,000 rows at a time, with any of the following columns. Any filters already applied will remain active."
        submitText="Export"
        columns={viewModel.exportColumns}
        onSubmit={viewModel.handleExport}
        show={viewModel.showExportModal}
        handleClose={() => viewModel.setShowExportModal(false)}
        disableExportTypes
      />
    </FBPage>
  )
}

function DoorInfo(props: { door: DoorSearchResult }) {
  const { door } = props
  return (
    <div className="flex flex-col gap-1">
      <div className="font-bold">{door.name}</div>
      <div className="text-xs flex flex-col gap-1">
        <div>
          {door.score} - {door.address}
        </div>
        <div>
          {door.city}, {door.state} {door.zipcode} {door.country}
        </div>
        {door.domain && <div>Domain: {door.domain}</div>}
        <div>
          Chain: {door.chain_name} ({door.chain_size} locations)
        </div>
        {door.type && <div>Type: {door.type}</div>}
      </div>
    </div>
  )
}

function BackButton({ hasPendingChanges }: { hasPendingChanges: boolean }) {
  return (
    <Link to="/importer">
      <FbButton
        onClick={(e) => {
          if (hasPendingChanges) {
            if (
              !confirm(
                'You have unsaved changes. Are you sure you want to go back?'
              )
            ) {
              e.preventDefault()
              e.stopPropagation()
            }
          }
        }}
        variant="secondary"
        size="sm"
        className="flex items-center gap-2 flex-shrink-0 whitespace-nowrap"
      >
        <ArrowLeft size={16} />
        Go Back
      </FbButton>
    </Link>
  )
}

function ChainInfo(props: { chain: ChainSearchResult }) {
  const { chain } = props
  return (
    <div className="flex flex-col gap-1">
      <div className="font-bold">{chain.name}</div>
      <div className="text-xs flex flex-col gap-1">
        <div>Score: {chain.score}</div>
        <div>
          {chain.domain} - {chain.type}
        </div>
      </div>
    </div>
  )
}
